import {useCallback, useEffect, useMemo, useState} from "react";
import Modal from "react-bootstrap/Modal";
import {Button, Form, Stack} from "react-bootstrap";
import {ContactTypeFilterUserData} from "../../../../types/ContactTypeFilter";
import SelectContactSource from "../../../../components/SelectContactSource";
import {useRestClient} from "../../../../client/SpmRestClient";
import {useAlerts} from "../../../../utils/UserAlerts";
import SelectContactType from "../../../../components/SelectContactType";
import SelectTargetGroups from "../../../../components/SelectTargetGroups";

export type ContactFilterFormProps = {
	contactFilter: ContactTypeFilterUserData;
	onSaved: (contactFilter: ContactTypeFilterUserData) => void;
	onClosed: () => void;
}

export default function ContactFilterForm({contactFilter, onSaved, onClosed}: ContactFilterFormProps) {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const [data, setData] = useState<ContactTypeFilterUserData>({...contactFilter});
	const [isChanged, setIsChanged] = useState(false);
	const [sourceId, setSourceId] = useState<string | null>();

	const isContactTypeValid = useMemo(() => !isNaN(data.contactTypeId), [data]);
	const isValid = useMemo(() => isContactTypeValid, [isContactTypeValid]);

	const onChanged = useCallback(
		() => {
			setIsChanged(true);
			setData({...data});
		},
		[data]
	);

	const save = useCallback(
		() => {
			onSaved(data);
		},
		[data, onSaved]
	);

	useEffect(
		() => {
			restClient.contactSources.all()
				.then(
					(sources) => {
						if (data.contactTypeId > 0) {
							restClient.contactTypes
								.load(data.contactTypeId)
								.then((t) => setSourceId(t?.source.id))
								.catch((e) => userAlerts.err(e));
						} else {
							setSourceId(sources[0].id);
						}
					}
				)
				.catch((e) => userAlerts.err(e));
		},
		[]
	);

	return (
		<Modal
			show={true}
			onHide={onClosed}
			backdropClassName="secondary-modal"
			dialogClassName="secondary-modal"
			centered={true}
		>
			<Modal.Header closeButton className={`text-default`}>
				<Modal.Title>Editace zobrazení kontaktu</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form onSubmit={save}>
					<Stack gap={2}>
						<div>
							<Form.Label>Zdroj</Form.Label>
							{
								sourceId && <SelectContactSource
									sourceId={sourceId}
									onChange={setSourceId}
								/>
							}
						</div>
						<div>
							<Form.Label>Druh pomoci</Form.Label>
							<SelectContactType
								sourceId={sourceId}
								selectedContactTypeId={data.contactTypeId}
								onChange={(id) => {
									data.contactTypeId = id ? Number(id) : NaN;
									onChanged();
								}}
							/>
							{
								isNaN(data.contactTypeId) && <small className="error">Zvolte druh pomoci</small>
							}
						</div>
						<div>
							<Form.Label>Cílové skupiny</Form.Label>
							<Stack gap={2}>
								<span className="small font-italic">
									Pozn.: Pokud nezvolíte žádnou cílovou skupinu, budou
									se zobrazovat všechny kontakty. Pokud zvolíte více
									cílových skupin, zobrazí se kontakty, které mají
									<u>všechny</u> vybrané cílové skupiny.
								</span>
								<div className="target-groups-scroller">
									<SelectTargetGroups
										selectedGroupIds={data.targetGroups}
										onChange={(ids) => {
											data.targetGroups = ids;
											onChanged();
										}}
									/>
								</div>
							</Stack>

						</div>
					</Stack>
				</Form>
				<div className="mt-2 d-flex justify-content-center gap-2">
					<Button
						variant="secondary"
						onClick={onClosed}
						className="mr-2"
					>Zavřít</Button>
					<Button onClick={save} disabled={!(isValid && isChanged)}>Uložit</Button>
				</div>
			</Modal.Body>
		</Modal>
	)
}
